<template>
  <div class="return-order-analysis purchase-mgmt-page">
    <div class="breadcrumb-box">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item>运营管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/client/returnOrderMgmt' }">退货订单</el-breadcrumb-item>
        <el-breadcrumb-item>退货订单分析</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="page-header">
      <div class="filter-card">
        <div class="filter-row">
          <div class="filter-item">
            <span class="filter-label">分析天数</span>
            <el-radio-group v-model="days" size="small" @change="onDaysChange">
              <el-radio-button :label="7">近7日</el-radio-button>
              <el-radio-button :label="30">近30日</el-radio-button>
              <el-radio-button :label="90">近90日</el-radio-button>
            </el-radio-group>
          </div>
          <div class="filter-item filter-item-product">
            <span class="filter-label">产品</span>
            <el-select
              v-model="currentProductName"
              placeholder="请选择产品"
              size="small"
              filterable
              clearable
              class="filter-select-product"
              popper-class="return-analysis-product-popper"
              :disabled="!catalogSorted.length"
              @change="onProductChange"
            >
              <el-option
                v-for="item in catalogSorted"
                :key="item.productName"
                :label="item.productName"
                :value="item.productName"
              >
                <div class="product-option-row">
                  <span class="col-name" :title="item.productName">
                    <i class="opt-label">名称</i>{{ item.productName }}
                  </span>
                  <span class="col-return">
                    <i class="opt-label">退货</i>{{ num(item.returnQty) }}
                  </span>
                  <span class="col-rate">
                    <i class="opt-label">率</i>{{ pct2(item.returnRate) }}
                  </span>
                </div>
              </el-option>
            </el-select>
          </div>
          <div class="filter-actions">
            <el-button type="primary" size="small" icon="el-icon-refresh" :loading="loading" @click="loadAnalysis">加载</el-button>
            <el-button type="warning" size="small" icon="el-icon-s-data" :loading="generating" @click="onGenerateClick">生成当前天数</el-button>
            <el-button size="small" icon="el-icon-s-opportunity" :loading="generating" @click="onGenerateAllClick">一键生成三档</el-button>
            <el-button size="small" icon="el-icon-back" @click="goBack">返回退货订单</el-button>
          </div>
        </div>
        <div class="analysis-tip">
          <span>数据区间：{{ dateRangeText }}</span>
          <span class="tip-gap">生成时间：{{ formatGeneratedAt(generatedAt) }}</span>
          <span class="tip-gap">状态：{{ statusLabel }}</span>
          <span class="tip-gap">退货率分母：business.unitsShippedT{{ days }}（与分析天数一致）</span>
        </div>
        <div class="meta-chips" v-if="metaList.length">
          <el-tag
            v-for="item in metaList"
            :key="item.days"
            size="mini"
            :type="tagType(item)"
            class="meta-chip"
          >
            {{ item.days }}日 · {{ item.status || "empty" }}
            <template v-if="item.generatedAt"> · {{ formatGeneratedAt(item.generatedAt) }}</template>
          </el-tag>
        </div>
      </div>
    </div>

    <div class="table-panel" v-loading="loading || generating" :element-loading-text="generating ? '生成中，请稍候...' : '加载中...'">
      <template v-if="currentProduct">
        <div class="product-mast">
          <div class="mast-main">
            <div class="mast-eyebrow">单品退货分析 · 近 {{ days }} 天</div>
            <div class="mast-title">{{ currentProduct.productName }}</div>
            <div class="mast-lede">
              退货率 = 同期退货件数 / business.unitsShippedT{{ days }}；维度分析仅针对当前所选产品（产品 / 颜色 / 机型 / SKU / 退货原因 / 客户备注词 / 行动计划）；点击行可查看退货明细；操作列可查看并复制该维度的退货分析提示词。
            </div>
          </div>
          <div class="mast-meta">
            <div class="mast-meta-row"><span>数据区间</span><strong>{{ dateRangeText }}</strong></div>
            <div class="mast-meta-row"><span>销量 / 退货</span><strong>{{ num(currentProduct.salesQty) }} / {{ num(currentProduct.returnQty) }}</strong></div>
            <div class="mast-meta-row"><span>退货率</span><strong>{{ pct2(currentProduct.returnRate) }}</strong></div>
            <div class="mast-meta-row"><span>机型维度</span><strong>{{ currentProduct.hasModel ? "有（展示机型 Tab）" : "无（隐藏机型 Tab）" }}</strong></div>
            <div class="mast-meta-row"><span>生成时间</span><strong>{{ formatGeneratedAt(generatedAt) }}</strong></div>
            <div class="mast-meta-row"><span>产品数</span><strong>{{ catalogSorted.length }}</strong></div>
          </div>
        </div>

        <div class="summary-row">
          <div class="summary-card">
            <div class="summary-label">销量（近{{ days }}日）</div>
            <div class="summary-value">{{ num(currentProduct.salesQty) }}</div>
            <div class="summary-hint">business.unitsShippedT{{ days }}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">退货量</div>
            <div class="summary-value">{{ num(currentProduct.returnQty) }}</div>
            <div class="summary-hint">近{{ days }}日 returnOrder</div>
          </div>
          <div class="summary-card accent-gt1">
            <div class="summary-label">退货率</div>
            <div class="summary-value">{{ pct2(currentProduct.returnRate) }}</div>
            <div class="summary-hint">退货 / 近{{ days }}天发货</div>
          </div>
          <div class="summary-card accent-multi">
            <div class="summary-label">主要问题</div>
            <div class="summary-value text-sm">{{ currentProduct.mainCategory || "—" }}</div>
            <div class="summary-hint">{{ pct(currentProduct.mainCategoryRatio) }}</div>
          </div>
          <div class="summary-card accent-repeat">
            <div class="summary-label">优先级</div>
            <div class="summary-value">{{ currentProduct.priority || "—" }}</div>
            <div class="summary-hint">{{ currentProduct.riskLevel || "—" }}风险</div>
          </div>
        </div>

        <div class="section-title">该产品退货概况</div>
        <div class="chart-grid">
          <div class="chart-card">
            <div class="section-title">退货原因 TOP</div>
            <div ref="chartReason" class="chart-box"></div>
          </div>
          <div class="chart-card">
            <div class="section-title">问题二次归类</div>
            <div ref="chartCategory" class="chart-box"></div>
          </div>
        </div>
        <div class="chart-grid chart-grid-3">
          <div class="chart-card">
            <div class="section-title">退货趋势</div>
            <div ref="chartTrend" class="chart-box sm"></div>
          </div>
          <div class="chart-card">
            <div class="section-title">处置详情</div>
            <div ref="chartDisposition" class="chart-box sm"></div>
          </div>
          <div class="chart-card">
            <div class="section-title">客户备注关键词</div>
            <div ref="chartKeywords" class="chart-box sm"></div>
          </div>
        </div>

        <div class="callout" v-if="currentProduct.coreProblem">
          <strong>核心问题：</strong>{{ currentProduct.coreProblem }}
        </div>
        <div class="judge-chain">
          <div class="judge-step">
            <div class="judge-label">问题判断</div>
            <div class="judge-body">{{ currentProduct.riskReason || "—" }}</div>
          </div>
          <div class="judge-step">
            <div class="judge-label">可能原因</div>
            <div class="judge-body" v-if="(currentProduct.possibleCauses || []).length">
              <div v-for="(c, idx) in currentProduct.possibleCauses" :key="'cause-' + idx">· {{ c }}</div>
            </div>
            <div class="judge-body" v-else>—</div>
          </div>
          <div class="judge-step">
            <div class="judge-label">客户反馈</div>
            <div class="judge-body" v-if="(currentProduct.representativeFeedback || []).length">
              <div v-for="(f, idx) in currentProduct.representativeFeedback" :key="'fb-' + idx">
                · [{{ f.category || "其他" }}] {{ f.text }}
              </div>
            </div>
            <div class="judge-body" v-else>暂无有效备注</div>
          </div>
        </div>

        <div class="section-title">维度分析（仅当前产品）</div>
        <el-radio-group v-model="currentDim" size="mini" class="dim-tabs" @change="onDimChange">
          <el-radio-button label="product">按产品</el-radio-button>
          <el-radio-button label="color">按颜色</el-radio-button>
          <el-radio-button v-if="currentProduct.hasModel" label="model">按机型</el-radio-button>
          <el-radio-button label="sku">按SKU</el-radio-button>
          <el-radio-button label="reason">按退货原因</el-radio-button>
          <el-radio-button label="keyword">按客户备注词</el-radio-button>
          <el-radio-button label="actionPlan">优化行动计划</el-radio-button>
        </el-radio-group>

        <div class="dim-table-wrap">
          <el-table
            v-if="currentDim !== 'actionPlan'"
            ref="dimTable"
            :key="'dim-' + currentDim"
            class="dim-table"
            :class="{ 'sku-table': currentDim === 'sku' }"
            :data="dimRows"
            border
            stripe
            size="mini"
            highlight-current-row
            height="500"
            empty-text="该维度暂无数据"
            @row-click="onDimRowClick"
          >
            <el-table-column type="index" label="序号" width="50" align="center" />
            <el-table-column
              :label="dimHeaderLabel"
              :width="dimValueColWidth"
              :class-name="currentDim === 'sku' ? 'sku-dim-col' : ''"
            >
              <template slot-scope="{ row }">
                <span :title="sliceTitle(row)">{{ dimValueText(row) }}</span>
                <el-tag v-if="row.lowSample" size="mini" type="warning">样本少</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="salesQty" label="销量" width="70" align="right">
              <template slot-scope="{ row }">{{ num(row.salesQty) }}</template>
            </el-table-column>
            <el-table-column prop="returnQty" label="退货" width="70" align="right" sortable>
              <template slot-scope="{ row }">{{ num(row.returnQty) }}</template>
            </el-table-column>
            <el-table-column prop="returnRate" label="退货率" width="80" align="right" sortable>
              <template slot-scope="{ row }">{{ pct2(row.returnRate) }}</template>
            </el-table-column>
            <el-table-column label="主要问题" width="200">
              <template slot-scope="{ row }">
                <span :title="row.mainCategory || ''">{{ clipText(row.mainCategory, 100) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="占比" width="64" align="right">
              <template slot-scope="{ row }">{{ pct(row.mainCategoryRatio) }}</template>
            </el-table-column>
            <el-table-column prop="riskLevel" label="风险" width="56" />
            <el-table-column prop="priority" label="优先级" width="64" />
            <el-table-column label="操作" width="128" align="center" class-name="prompt-op-col">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" @click.native.stop="openDimPrompt(row)">查看退货提示词</el-button>
              </template>
            </el-table-column>
          </el-table>

          <el-table
            v-else
            ref="planTable"
            :key="'plan-' + currentProductName"
            class="dim-table action-plan-table"
            :data="actionPlanRows"
            border
            stripe
            size="mini"
            highlight-current-row
            height="500"
            empty-text="暂无行动项"
            @row-click="onPlanRowClick"
          >
            <el-table-column type="index" label="序号" width="50" align="center" />
            <el-table-column prop="priority" label="优先级" width="56" />
            <el-table-column prop="scope" label="范围" width="72" />
            <el-table-column label="名称" width="120">
              <template slot-scope="{ row }">
                <span :title="row.name || ''">{{ clipText(row.name, 30) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="核心问题" width="140">
              <template slot-scope="{ row }">
                <span :title="row.coreProblem || ''">{{ clipText(row.coreProblem, 100) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="动作" width="500">
              <template slot-scope="{ row }">
                <span :title="row.action || ''">{{ clipText(row.action, 100) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="direction" label="类型" width="80" show-overflow-tooltip />
            <el-table-column label="退货率" width="72" align="right">
              <template slot-scope="{ row }">{{ pct2(row.returnRate) }}</template>
            </el-table-column>
            <el-table-column label="退货" width="56" align="right">
              <template slot-scope="{ row }">{{ num(row.returnQty) }}</template>
            </el-table-column>
            <el-table-column prop="riskLevel" label="风险" width="56" />
            <el-table-column label="操作" width="128" align="center" class-name="prompt-op-col">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" @click.native.stop="openPlanPrompt(row)">查看退货提示词</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="detail-panel" v-if="activeSlice">
          <div class="detail-head">
            <div>
              <div class="detail-title">{{ sliceTitle(activeSlice) }}</div>
              <div class="detail-meta">
                销量 {{ num(activeSlice.salesQty) }} · 退货 {{ num(activeSlice.returnQty) }} · 退货率
                {{ pct2(activeSlice.returnRate) }} · {{ activeSlice.riskLevel }} / {{ activeSlice.priority }}
              </div>
            </div>
            <el-button size="mini" @click="closeDetail">关闭</el-button>
          </div>
          <div class="callout" v-if="activeSlice.coreProblem">
            <strong>核心问题：</strong>{{ activeSlice.coreProblem }}
          </div>
          <div class="chart-grid">
            <div class="chart-card">
              <div class="section-title">切片退货原因</div>
              <div ref="chartSliceReason" class="chart-box sm"></div>
            </div>
            <div class="chart-card">
              <div class="section-title">切片问题归类</div>
              <div ref="chartSliceCategory" class="chart-box sm"></div>
            </div>
          </div>
          <div class="section-title">退货记录明细</div>
          <div class="detail-toolbar">
            <span>共 {{ activeDetailRows.length }} 条，筛选后 {{ filteredDetailRows.length }} 条</span>
            <el-input v-model="detailSearch" size="mini" clearable placeholder="筛选订单号 / 备注" class="detail-search" />
          </div>
          <el-table :data="filteredDetailRows" border stripe size="mini" height="500" empty-text="暂无退货明细">
            <el-table-column prop="returnDate" label="退货日" width="110" />
            <el-table-column prop="orderId" label="订单号" width="160" show-overflow-tooltip />
            <el-table-column prop="productColor" label="颜色" width="90" show-overflow-tooltip />
            <el-table-column prop="model" label="机型" width="110" show-overflow-tooltip />
            <el-table-column prop="sku" label="SKU" width="140" show-overflow-tooltip />
            <el-table-column label="原因" width="120" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ reasonZh(row.reason) }}</template>
            </el-table-column>
            <el-table-column prop="category" label="归类" width="120" show-overflow-tooltip />
            <el-table-column prop="disposition" label="处置" width="100" show-overflow-tooltip />
            <el-table-column prop="quantity" label="数量" width="60" align="right" />
            <el-table-column prop="comments" label="客户备注" min-width="280" show-overflow-tooltip />
          </el-table>
        </div>
      </template>
      <el-empty v-else description="暂无分析数据，请先选择天数并点击生成" />
    </div>

    <el-dialog
      title="退货提示词"
      :visible.sync="promptVisible"
      width="760px"
      append-to-body
      custom-class="return-prompt-dialog"
    >
      <div class="prompt-meta">{{ promptTitle }} · {{ promptCount }} 条退货记录</div>
      <el-input v-model="promptText" type="textarea" :rows="18" readonly />
      <span slot="footer">
        <el-button size="small" @click="promptVisible = false">关闭</el-button>
        <el-button size="small" type="primary" @click="copyReturnPrompt">复制提示词</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from "echarts";
import requestModules from "@/request/requestModules";
import { shopTypeFromName } from "@/utils/shopIdentity";

const RETURN_PROMPT_HEAD = `你是一名资深的亚马逊运营分析专家。请分析我提供的亚马逊退货订单数据，重点根据英文客户备注（Customer Comments）识别客户退货的主要原因，并以中文输出分析报告。

分析要求：

原因分类： 将退货原因归类为产品质量、尺寸或机型不适配、产品与描述不符、外观不满意、功能问题、客户购买错误、物流或包装问题、其他原因、无法判断等类别。

数据统计： 统计各类退货原因的记录数、退货件数及占比，并按发生频率排序。

典型反馈： 提取典型英文备注，附中文翻译，说明客户反映的具体问题。

问题分析： 结合 SKU、ASIN 等字段，识别反复出现的问题及可能涉及的产品、页面或适配性问题。

改进建议： 根据分析结果，提出具体、可执行的优化措施，并按优先级排序。

输出要求：

全部分析结论使用中文，保留典型英文原文并附中文翻译。

相似的退货原因应合并分类，避免重复统计。

一条记录只计入一个主要原因，其他原因可作为次要原因。

空白或信息不足的备注单独统计，不得强行推断。

明确区分客户明确反馈与推测的根本原因，不得编造结论。

区分订单记录数和实际退货件数；没有销售总量时，不得计算退货率。

最终输出：总体结论、退货原因统计表、主要问题分析、SKU问题分析（如数据支持）、改进建议。

退货明细如下：`;

export default {
  name: "ReturnOrderAnalysis",
  data() {
    return {
      days: 90,
      loading: false,
      generating: false,
      status: "empty",
      generatedAt: "",
      beginDate: "",
      endDate: "",
      meta: {},
      overview: {},
      catalog: [],
      products: [],
      reasonLabels: {},
      metaList: [],
      currentProductName: "",
      currentDim: "product",
      activeSlice: null,
      activeDetailRows: [],
      detailSearch: "",
      filterDim: "product",
      charts: {},
      promptVisible: false,
      promptTitle: "",
      promptText: "",
      promptCount: 0,
    };
  },
  computed: {
    statusLabel() {
      if (this.status === "ready") return "已就绪";
      if (this.status === "running") return "生成中";
      if (this.status === "failed") return "生成失败";
      return "未生成";
    },
    currentProduct() {
      return this.products.find(p => p.productName === this.currentProductName) || null;
    },
    catalogSorted() {
      const shopType = this.currentShopType;
      const list = (this.catalog || []).filter(item => this.catalogItemMatchesShop(item, shopType));
      return (list || []).slice().sort((a, b) => {
        const aq = (a && a.returnQty) || 0;
        const bq = (b && b.returnQty) || 0;
        if (bq !== aq) return bq - aq;
        const ar = a && a.returnRate != null && isFinite(a.returnRate) ? a.returnRate : -1;
        const br = b && b.returnRate != null && isFinite(b.returnRate) ? b.returnRate : -1;
        return br - ar;
      });
    },
    currentShopType() {
      const raw = localStorage.getItem("shopType");
      if (raw == null || raw === "") return "";
      return String(raw);
    },
    dateRangeText() {
      const m = this.meta || {};
      const begin =
        this.beginDate ||
        (m.dateRange && m.dateRange.begin) ||
        m.beginDate ||
        "—";
      const end =
        this.endDate ||
        (m.dateRange && m.dateRange.end) ||
        m.endDate ||
        "—";
      return `${begin} ~ ${end}`;
    },
    dimHeaderLabel() {
      const map = {
        product: "产品",
        color: "颜色",
        model: "机型",
        sku: "SKU",
        reason: "退货原因",
        keyword: "客户备注词",
        actionPlan: "行动项",
      };
      return map[this.currentDim] || "维度值";
    },
    dimValueColWidth() {
      return this.currentDim === "sku" ? 300 : 180;
    },
    dimRows() {
      const p = this.currentProduct;
      if (!p) return [];
      let rows = [];
      if (this.currentDim === "product") rows = [this.productAsSlice(p)];
      else if (this.currentDim === "model") rows = p.byModel || [];
      else if (this.currentDim === "sku") rows = p.bySku || [];
      else if (this.currentDim === "reason") rows = p.byReason || [];
      else if (this.currentDim === "keyword") rows = p.byKeyword || [];
      else if (this.currentDim === "actionPlan") rows = [];
      else rows = p.byColor || [];
      return this.sortByReturnRateDesc(rows);
    },
    actionPlanRows() {
      return this.sortByReturnRateDesc((this.currentProduct && this.currentProduct.actionPlan) || []);
    },
    filteredDetailRows() {
      const q = String(this.detailSearch || "").trim().toLowerCase();
      if (!q) return this.activeDetailRows;
      return this.activeDetailRows.filter(r => {
        const blob = [r.orderId, r.comments, r.reason, r.category, r.productColor, r.model, r.sku]
          .concat(r.keywords || [])
          .join(" ")
          .toLowerCase();
        return blob.indexOf(q) >= 0;
      });
    },
  },
  mounted() {
    const queryDays = Number((this.$route.query || {}).days);
    if ([7, 30, 90].includes(queryDays)) this.days = queryDays;
    this.loadAnalysis();
    window.addEventListener("resize", this.resizeCharts);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCharts);
    Object.keys(this.charts).forEach(k => {
      if (this.charts[k]) this.charts[k].dispose();
    });
  },
  methods: {
    goBack() {
      this.$router.push("/client/returnOrderMgmt");
    },
    num(n) {
      return (n || 0).toLocaleString();
    },
    clipText(text, maxLen) {
      const s = String(text == null ? "" : text).trim();
      if (!s) return "—";
      const limit = maxLen || 30;
      if (s.length <= limit) return s;
      return s.slice(0, limit) + "…";
    },
    sortByReturnRateDesc(rows) {
      return (rows || []).slice().sort((a, b) => {
        const ar = a && a.returnRate != null && isFinite(a.returnRate) ? a.returnRate : -1;
        const br = b && b.returnRate != null && isFinite(b.returnRate) ? b.returnRate : -1;
        if (br !== ar) return br - ar;
        return (b.returnQty || 0) - (a.returnQty || 0);
      });
    },
    catalogItemMatchesShop(item, shopType) {
      if (!shopType) return true;
      const want = String(shopType);
      const types = (item && item.shopTypes) || [];
      if (types.length) return types.some(t => String(t) === want);
      const product = (this.products || []).find(p => p.productName === item.productName);
      if (!product) return false;
      const pTypes = product.shopTypes || [];
      if (pTypes.length) return pTypes.some(t => String(t) === want);
      const rows = product.detailRows || [];
      if (!rows.length) return false;
      return rows.some(r => {
        if (r.shopType != null && r.shopType !== "" && String(r.shopType) === want) return true;
        const mapped = shopTypeFromName(r.shopName);
        return mapped && String(mapped) === want;
      });
    },
    pct(r) {
      if (r == null || !isFinite(r)) return "—";
      return (r * 100).toFixed(1) + "%";
    },
    pct2(r) {
      if (r == null || !isFinite(r)) return "—";
      return (r * 100).toFixed(2) + "%";
    },
    reasonZh(code) {
      const key = String(code || "").trim().toUpperCase();
      return this.reasonLabels[key] || String(code || "未知原因");
    },
    formatGeneratedAt(v) {
      if (!v) return "—";
      return String(v).replace("T", " ").slice(0, 19);
    },
    tagType(item) {
      if (item.status === "ready") return "success";
      if (item.status === "running") return "warning";
      if (item.status === "failed") return "danger";
      return "info";
    },
    productAsSlice(p) {
      return {
        key: p.key || `productName::${p.productName}`,
        dimension: "productName",
        title: p.productName,
        productName: p.productName,
        returnQty: p.returnQty,
        salesQty: p.salesQty || 0,
        returnRate: p.returnRate,
        mainCategory: p.mainCategory,
        mainCategoryRatio: p.mainCategoryRatio,
        riskLevel: p.riskLevel,
        priority: p.priority,
        lowSample: p.lowSample,
        coreProblem: p.coreProblem,
        categoryStats: p.categoryStats || [],
        reasonStats: p.reasonStats || [],
        recommendations: p.recommendations || [],
        detailRows: p.detailRows || [],
      };
    },
    sliceTitle(s) {
      if (!s) return "—";
      if (this.currentDim === "reason" || s.dimension === "reason") return this.reasonZh(s.title);
      return s.title;
    },
    dimValueText(row) {
      const title = this.sliceTitle(row);
      // SKU 完整展示，不做字数截断
      if (this.currentDim === "sku") return title || "—";
      return this.clipText(title, 30);
    },
    onDaysChange() {
      this.closeDetail();
      this.loadAnalysis();
    },
    onProductChange() {
      this.closeDetail();
      this.currentDim = "product";
      this.$nextTick(() => this.renderOverviewCharts());
    },
    onDimChange() {
      this.closeDetail();
      if (this.currentDim === "model" && this.currentProduct && !this.currentProduct.hasModel) {
        this.currentDim = "product";
      }
      this.$nextTick(() => this.relayoutDimTable());
    },
    closeDetail() {
      this.activeSlice = null;
      this.activeDetailRows = [];
      this.detailSearch = "";
    },
    applyPayload(mdata) {
      this.status = mdata.status || "empty";
      this.generatedAt = mdata.generatedAt || "";
      this.beginDate = mdata.beginDate || "";
      this.endDate = mdata.endDate || "";
      this.meta = mdata.meta || {};
      if (!this.beginDate && this.meta.dateRange) this.beginDate = this.meta.dateRange.begin || "";
      if (!this.endDate && this.meta.dateRange) this.endDate = this.meta.dateRange.end || "";
      this.overview = mdata.overview || {};
      this.catalog = mdata.catalog || [];
      this.products = mdata.products || [];
      this.reasonLabels = mdata.reasonLabels || {};
      this.metaList = mdata.metaList || this.metaList || [];
      if (this.catalog.length) {
        const exists = this.catalogSorted.some(c => c.productName === this.currentProductName);
        if (!exists) {
          const first = this.catalogSorted[0];
          this.currentProductName = first ? first.productName : "";
        }
      } else {
        this.currentProductName = "";
      }
      this.closeDetail();
      this.$nextTick(() => this.renderOverviewCharts());
    },
    loadAnalysis() {
      this.loading = true;
      requestModules
        .fetchReturnOrderAnalysis({ days: this.days })
        .then(res => {
          if (res.mdata && res.mdata.metaList) this.metaList = res.mdata.metaList;
          if (res.code !== 0) {
            this.status = (res.mdata && res.mdata.status) || "empty";
            this.catalog = [];
            this.products = [];
            this.currentProductName = "";
            this.closeDetail();
            if (res.message) this.$message.warning(res.message);
            return;
          }
          this.applyPayload(res.mdata || {});
        })
        .catch(() => {
          this.$message.error("加载退货分析失败");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onGenerateClick() {
      this.$confirm(`确认生成近 ${this.days} 日退货分析？将覆盖该天数已有结果。`, "生成确认", {
        type: "warning",
      })
        .then(() => this.generate([this.days]))
        .catch(() => {});
    },
    onGenerateAllClick() {
      this.$confirm("确认一键生成 7 / 30 / 90 日三档分析？耗时可能较长。", "生成确认", {
        type: "warning",
      })
        .then(() => this.generate([7, 30, 90]))
        .catch(() => {});
    },
    generate(daysList) {
      this.generating = true;
      const payload =
        daysList.length === 1
          ? { days: daysList[0] }
          : { daysList: JSON.stringify(daysList) };
      requestModules
        .generateReturnOrderAnalysis(payload)
        .then(res => {
          if (res.mdata && res.mdata.metaList) this.metaList = res.mdata.metaList;
          if (res.code !== 0) {
            this.$message.error(res.message || "生成失败");
            return;
          }
          this.$message.success(res.message || "生成成功");
          if (res.mdata && res.mdata.status === "ready" && Number(res.mdata.days) === Number(this.days)) {
            this.applyPayload(res.mdata);
          } else {
            this.loadAnalysis();
          }
        })
        .catch(() => {
          this.$message.error("生成退货分析失败");
        })
        .finally(() => {
          this.generating = false;
        });
    },
    filterDetailRows(rows, slice, filterDim) {
      const list = rows || [];
      if (!slice) return list;
      if (filterDim === "product" || slice.dimension === "productName") {
        return list.filter(r => r.productName === slice.productName || r.productName === slice.title);
      }
      if (filterDim === "color") return list.filter(r => r.productColor === slice.title);
      if (filterDim === "model") return list.filter(r => r.model === slice.title);
      if (filterDim === "sku") {
        return list.filter(r => {
          const sku = r.sku || "";
          return sku === slice.title || (slice.title === "(未填写SKU)" && !sku);
        });
      }
      if (filterDim === "reason") {
        return list.filter(r => String(r.reason || "").toUpperCase() === String(slice.title || "").toUpperCase());
      }
      if (filterDim === "keyword") return list.filter(r => (r.keywords || []).indexOf(slice.title) >= 0);
      return list;
    },
    openSlice(slice, filterDim) {
      if (!slice || !this.currentProduct) return;
      this.activeSlice = slice;
      this.filterDim = filterDim || this.currentDim;
      this.activeDetailRows = this.filterDetailRows(this.currentProduct.detailRows || [], slice, this.filterDim);
      this.detailSearch = "";
      this.$nextTick(() => this.renderSliceCharts());
    },
    onDimRowClick(row) {
      this.openSlice(row, this.currentDim);
    },
    resolvePlanSlice(row) {
      if (!this.currentProduct || !row) return null;
      let slice = null;
      let filterDim = "product";
      if (row.scope === "产品整体") {
        slice = this.productAsSlice(this.currentProduct);
        filterDim = "product";
      } else if (row.scope === "颜色") {
        slice = (this.currentProduct.byColor || []).find(s => s.title === row.name) || null;
        filterDim = "color";
      } else if (row.scope === "机型") {
        slice = (this.currentProduct.byModel || []).find(s => s.title === row.name) || null;
        filterDim = "model";
      }
      if (!slice) {
        slice =
          (this.currentProduct.byColor || []).find(s => s.title === row.name) ||
          (this.currentProduct.byModel || []).find(s => s.title === row.name) ||
          this.productAsSlice(this.currentProduct);
        if (slice.dimension === "color") filterDim = "color";
        else if (slice.dimension === "model") filterDim = "model";
        else filterDim = "product";
      }
      return { slice, filterDim };
    },
    promptField(value) {
      const text = String(value == null ? "" : value).trim();
      return text || "—";
    },
    promptComment(value) {
      const text = String(value == null ? "" : value).trim();
      return text || "（空白）";
    },
    promptPieceCount(row) {
      const n = Number(row && row.quantity);
      return Number.isFinite(n) && n > 0 ? n : 1;
    },
    buildReturnPrompt(scopeLabel, rows, slice) {
      const list = rows || [];
      const pieces = list.reduce((sum, row) => sum + this.promptPieceCount(row), 0);
      const sales = slice && slice.salesQty != null && isFinite(Number(slice.salesQty)) ? Number(slice.salesQty) : null;
      const lines = [
        RETURN_PROMPT_HEAD,
        "",
        `当前分析范围：${scopeLabel}`,
        `同期销量：${sales == null ? "未提供（不得计算退货率）" : sales}`,
        `退货记录数：${list.length}`,
        `退货件数：${pieces}`,
        "",
      ];
      if (!list.length) {
        lines.push("（该范围暂无退货明细）");
        return lines.join("\n");
      }
      list.forEach((row, index) => {
        lines.push(`【${index + 1}】`);
        lines.push(`退货日期：${this.promptField(row.returnDate)}`);
        lines.push(`订单号：${this.promptField(row.orderId)}`);
        lines.push(`颜色：${this.promptField(row.productColor)}`);
        lines.push(`机型：${this.promptField(row.model)}`);
        lines.push(`SKU：${this.promptField(row.sku)}`);
        lines.push(`ASIN：${this.promptField(row.asin)}`);
        lines.push(`原因：${this.promptField(this.reasonZh(row.reason))}`);
        lines.push(`归类：${this.promptField(row.category)}`);
        lines.push(`退货件数：${this.promptPieceCount(row)}`);
        lines.push(`客户备注：${this.promptComment(row.comments)}`);
        lines.push("");
      });
      return lines.join("\n").replace(/\n$/, "");
    },
    showReturnPrompt(title, rows, slice) {
      const list = rows || [];
      this.promptTitle = title || "—";
      this.promptCount = list.length;
      this.promptText = this.buildReturnPrompt(title || "—", list, slice);
      this.promptVisible = true;
    },
    openDimPrompt(row) {
      if (!row || !this.currentProduct) return;
      const rows = this.filterDetailRows(this.currentProduct.detailRows || [], row, this.currentDim);
      this.showReturnPrompt(`${this.dimHeaderLabel} · ${this.sliceTitle(row)}`, rows, row);
    },
    openPlanPrompt(row) {
      const resolved = this.resolvePlanSlice(row);
      if (!resolved) return;
      const rows = this.filterDetailRows(
        this.currentProduct.detailRows || [],
        resolved.slice,
        resolved.filterDim
      );
      const title = `${row.scope || "行动计划"} · ${row.name || this.sliceTitle(resolved.slice)}`;
      this.showReturnPrompt(title, rows, resolved.slice);
    },
    copyByExecCommand(text) {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "readonly");
      area.style.position = "fixed";
      area.style.left = "-9999px";
      document.body.appendChild(area);
      area.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch (e) {
        ok = false;
      }
      document.body.removeChild(area);
      return ok;
    },
    copyReturnPrompt() {
      const text = this.promptText || "";
      if (!text) {
        this.$message.warning("暂无提示词");
        return;
      }
      const done = () => this.$message.success("已复制退货提示词");
      const fail = () => this.$message.error("复制失败，请在文本框中手动复制");
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
          if (this.copyByExecCommand(text)) done();
          else fail();
        });
        return;
      }
      if (this.copyByExecCommand(text)) done();
      else fail();
    },
    onPlanRowClick(row) {
      const resolved = this.resolvePlanSlice(row);
      if (!resolved) return;
      const slice = resolved.slice;
      const filterDim = resolved.filterDim;
      // 详情标题优先展示行动计划名称，并附上建议动作
      const detailSlice = Object.assign({}, slice, {
        title: row.name || slice.title,
        coreProblem: row.action
          ? `【${row.scope || "行动"}】${row.coreProblem || slice.coreProblem || ""}；建议：${row.action}`
          : slice.coreProblem,
        priority: row.priority || slice.priority,
        riskLevel: row.riskLevel || slice.riskLevel,
        returnQty: row.returnQty != null ? row.returnQty : slice.returnQty,
        returnRate: row.returnRate != null ? row.returnRate : slice.returnRate,
        salesQty: row.salesQty != null ? row.salesQty : slice.salesQty,
      });
      this.openSlice(detailSlice, filterDim);
    },
    disposeChart(key) {
      if (this.charts[key]) {
        this.charts[key].dispose();
        this.charts[key] = null;
      }
    },
    makeBar(refName, items, color) {
      const el = this.$refs[refName];
      if (!el) return;
      this.disposeChart(refName);
      if (!items || !items.length) return;
      const chart = echarts.init(el);
      chart.setOption({
        color: [color || "#409EFF"],
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: 8, right: 24, top: 12, bottom: 4, containLabel: true },
        xAxis: { type: "value", splitLine: { lineStyle: { color: "#ebeef5" } } },
        yAxis: {
          type: "category",
          data: items.map(i => i.name).reverse(),
          axisLabel: { width: 100, overflow: "truncate" },
        },
        series: [
          {
            type: "bar",
            barMaxWidth: 16,
            data: items.map(i => i.count).reverse(),
            label: { show: true, position: "right", fontSize: 11 },
          },
        ],
      });
      this.charts[refName] = chart;
    },
    makePie(refName, items) {
      const el = this.$refs[refName];
      if (!el) return;
      this.disposeChart(refName);
      if (!items || !items.length) return;
      const chart = echarts.init(el);
      chart.setOption({
        tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
        legend: { type: "scroll", bottom: 0, textStyle: { fontSize: 11 } },
        series: [
          {
            type: "pie",
            radius: ["36%", "60%"],
            center: ["50%", "44%"],
            label: { formatter: "{d}%", fontSize: 11 },
            data: items.map(i => ({ name: i.name, value: i.count })),
          },
        ],
      });
      this.charts[refName] = chart;
    },
    makeLine(refName, trend) {
      const el = this.$refs[refName];
      if (!el) return;
      this.disposeChart(refName);
      const list = trend || [];
      if (!list.length) return;
      const chart = echarts.init(el);
      chart.setOption({
        color: ["#409EFF"],
        tooltip: { trigger: "axis" },
        grid: { left: 8, right: 12, top: 16, bottom: 4, containLabel: true },
        xAxis: {
          type: "category",
          data: list.map(t => String(t.date || "").slice(5)),
          axisLabel: { fontSize: 10, rotate: 45 },
        },
        yAxis: {
          type: "value",
          splitLine: { lineStyle: { color: "#ebeef5" } },
        },
        series: [
          {
            type: "line",
            smooth: true,
            showSymbol: false,
            areaStyle: { color: "rgba(64,158,255,.12)" },
            data: list.map(t => t.count),
          },
        ],
      });
      this.charts[refName] = chart;
    },
    keywordItems(list) {
      return (list || []).slice(0, 10).map(k => ({
        name: k.keyword || k.name || "—",
        count: k.count || 0,
      }));
    },
    reasonItemsZh(stats) {
      return (stats || []).map(x => ({
        name: this.reasonZh(x.name),
        count: x.count,
      }));
    },
    renderOverviewCharts() {
      if (!this.currentProduct) return;
      const p = this.currentProduct;
      this.makeBar("chartReason", this.reasonItemsZh((p.reasonStats || []).slice(0, 10)), "#409EFF");
      this.makePie("chartCategory", p.categoryStats || []);
      this.makeLine("chartTrend", p.trend || []);
      this.makePie("chartDisposition", p.dispositionStats || []);
      this.makeBar("chartKeywords", this.keywordItems(p.keywords || p.commentKeywords || []), "#67C23A");
      this.$nextTick(() => this.relayoutDimTable());
    },
    relayoutDimTable() {
      const table = this.$refs.dimTable || this.$refs.planTable;
      if (table && typeof table.doLayout === "function") table.doLayout();
    },
    renderSliceCharts() {
      if (!this.activeSlice) return;
      this.makeBar("chartSliceReason", this.reasonItemsZh(this.activeSlice.reasonStats || []), "#E6A23C");
      this.makePie("chartSliceCategory", this.activeSlice.categoryStats || []);
    },
    resizeCharts() {
      Object.keys(this.charts).forEach(k => {
        if (this.charts[k]) this.charts[k].resize();
      });
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/styles/purchaseMgmtPage.scss";

.return-order-analysis {
  .breadcrumb-box {
    flex-shrink: 0;
    padding: 0 4px 10px;
  }

  /* 分析页内容较长：覆盖公共样式的 overflow:hidden，允许整块内容滚动
     同时取消 flex 列布局，避免子块（维度表）被 flex-shrink 压成 0 高度 */
  .table-panel {
    display: block;
    overflow: auto;
  }

  .analysis-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
  }
  .tip-gap {
    margin-left: 16px;
  }
  .meta-chips {
    margin-top: 8px;
  }
  .meta-chip {
    margin-right: 6px;
  }
  .product-mast {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
  }
  .mast-eyebrow {
    font-size: 12px;
    color: #909399;
    margin-bottom: 4px;
  }
  .mast-title {
    font-size: 20px;
    font-weight: 600;
    color: #303133;
    line-height: 1.35;
    margin-bottom: 6px;
  }
  .mast-lede {
    font-size: 12px;
    color: #606266;
    line-height: 1.6;
    max-width: 720px;
  }
  .mast-meta {
    flex: 0 0 260px;
    background: #f8fafc;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 10px 12px;
  }
  .mast-meta-row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 12px;
    line-height: 1.8;
    span {
      color: #909399;
      flex-shrink: 0;
    }
    strong {
      color: #303133;
      font-weight: 600;
      text-align: right;
      word-break: break-all;
    }
  }
  .summary-row {
    display: grid;
    grid-template-columns: repeat(5, minmax(120px, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }
  .summary-card {
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px 14px;
    .summary-label {
      font-size: 12px;
      color: #909399;
      margin-bottom: 6px;
    }
    .summary-value {
      font-size: 22px;
      font-weight: 600;
      color: #303133;
      &.text-sm {
        font-size: 15px;
        line-height: 1.35;
      }
    }
    .summary-hint {
      margin-top: 4px;
      font-size: 11px;
      color: #c0c4cc;
    }
    &.accent-gt1 {
      background: #fdf6ec;
      .summary-value {
        color: #e6a23c;
      }
    }
    &.accent-multi {
      background: #ecf5ff;
      .summary-value {
        color: #409eff;
      }
    }
    &.accent-repeat {
      background: #f4ecff;
      .summary-value {
        color: #9b59b6;
      }
    }
  }
  .chart-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }
  .chart-grid-3 {
    grid-template-columns: repeat(3, 1fr);
  }
  .chart-card {
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 10px 12px;
    .section-title {
      margin-top: 0;
    }
  }
  .chart-box {
    height: 260px;
    &.sm {
      height: 220px;
    }
  }
  .judge-chain {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin: 0 0 14px;
  }
  .judge-step {
    background: #f8fafc;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 10px 12px;
  }
  .judge-label {
    font-size: 11px;
    color: #909399;
    letter-spacing: 0.04em;
    margin-bottom: 6px;
  }
  .judge-body {
    font-size: 13px;
    color: #606266;
    line-height: 1.55;
    word-break: break-word;
  }
  .section-title {
    margin: 12px 0 8px;
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    padding-left: 8px;
    border-left: 3px solid #409eff;
  }
  .dim-tabs {
    margin-bottom: 8px;
  }
  .dim-table-wrap {
    width: fit-content;
    max-width: 100%;
    overflow-x: hidden;
    flex-shrink: 0;
    min-height: 120px;
  }
  /* 适中固定宽，不铺满整行；列宽之和与此一致，避免横向滚动 */
  .dim-table.el-table {
    width: 963px !important;
  }
  .dim-table.sku-table.el-table {
    width: 1083px !important;
  }
  .dim-table.action-plan-table.el-table {
    width: 1331px !important;
  }
  .dim-table ::v-deep .cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* 按 SKU：列宽 300，单行完整展示，不换行、不做省略截断 */
  .dim-table ::v-deep td.sku-dim-col .cell {
    white-space: nowrap;
    overflow: visible;
    text-overflow: clip;
  }
  .dim-table ::v-deep td.prompt-op-col .cell {
    overflow: visible;
    text-overflow: clip;
  }
  .callout {
    background: #ecf5ff;
    border-left: 3px solid #409eff;
    padding: 10px 12px;
    margin: 10px 0;
    font-size: 13px;
    color: #606266;
  }
  .detail-panel {
    margin-top: 16px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px;
  }
  .detail-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }
  .detail-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }
  .detail-meta {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
  }
  .detail-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    font-size: 12px;
    color: #909399;
  }
  .detail-search {
    width: 220px;
  }
  .filter-item-product {
    .filter-select-product {
      width: 360px !important;
      .el-input {
        width: 360px !important;
      }
    }
  }
}
/* 下拉挂到 body，需全局样式；单行三列左对齐（标签与值同行） */
.return-prompt-dialog {
  .prompt-meta {
    margin-bottom: 8px;
    font-size: 12px;
    color: #909399;
  }
  .el-textarea__inner {
    font-family: inherit;
    line-height: 1.55;
    color: #303133;
  }
}
.return-analysis-product-popper {
  min-width: 420px !important;
  .el-select-dropdown__item {
    height: auto;
    line-height: 1.4;
    padding: 8px 12px;
  }
  .product-option-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 88px 88px;
    column-gap: 12px;
    align-items: center;
    width: 100%;
    text-align: left;
    font-size: 13px;
    color: #303133;
    span {
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .opt-label {
      font-style: normal;
      color: #909399;
      font-size: 12px;
      margin-right: 4px;
    }
    .col-return,
    .col-rate {
      font-variant-numeric: tabular-nums;
    }
  }
}
@media (max-width: 1100px) {
  .return-order-analysis {
    .product-mast {
      flex-direction: column;
    }
    .mast-meta {
      flex: none;
      width: 100%;
    }
    .summary-row {
      grid-template-columns: repeat(2, 1fr);
    }
    .chart-grid,
    .chart-grid-3,
    .judge-chain {
      grid-template-columns: 1fr;
    }
  }
}
</style>
